/// <reference path="../_references.ts"/>

module App.Administrative.Tags {
	import Appointment = App.Appointments.Appointment;
	import SelectedAppointment = App.Appointments.SelectedAppointment;
	import TitleBarService = App.Helpers.TitleBarService;
	import ServiceRequest = App.Appointments.ServiceRequest;
	import Customer = App.Appointments.Customer;

	export class TagsController {
		customer: Customer = null;
		serviceRequest: ServiceRequest = null;
		isSaving: boolean = false;
		tagEdit: boolean = false;
		canCustomerSelect: boolean = false;
		canSrSelect: boolean = false;
		isLoading: boolean = true;
		srTags: Tag[] = [];
		newTag: Tag = null;
		newTagType: string = null;
		customerTags: Tag[] = [];
		tagColors: TagColor[] = [];

		static $inject: string[] = ['$q', 'SelectedAppointment', 'TagsService', 'TitleBarService', 'ToastService', 'PermissionService'];

		constructor(
			public $q: ng.IQService,
			private SelectedAppointment: SelectedAppointment,
			private TagsService: TagsService,
			private TitleBarService: TitleBarService,
			private ToastService: App.Utils.ToastService,
			protected PermissionService: App.Helpers.PermissionService,
		) {
			TitleBarService.set('Appointment');
			this.canCustomerSelect = PermissionService.hasPermission('web.field.administrative.tagging.customer.select');
			this.canSrSelect = PermissionService.hasPermission('web.field.administrative.tagging.service-request.select');

			SelectedAppointment.getAppointment().then((appointment) => {
				this.setAppointment(appointment);
			});
		}

		setAppointment(appointment: Appointment) {
			this.customer = appointment.service_request.customer;
			this.serviceRequest = appointment.service_request;

			this.initialize();
		}

		initialize() {
			const promises = {
				customerTags: this.TagsService.fetchCustomerTags(),
				tagColors: this.TagsService.fetchTagColors(),
				srTags: this.TagsService.fetchSRTags()
			};

			this.$q.all(promises).then((response) => {
				this.customerTags = response.customerTags.data;
				this.tagColors = response.tagColors.data;
				this.srTags = response.srTags.data;
			}).finally(() => this.isLoading = false);
		}

		getCustomerTagIndex(id: number) {
			return this.customer.tags.findIndex(function(t) {
				return t.id === id;
			});
		};

		getSrTagIndex(id: number) {
			return this.serviceRequest.tags.findIndex(function(t) {
				return t.id === id;
			});
		};

		selectCustomerTag(tag: Tag) {
			let index = this.getCustomerTagIndex(tag.id);

			if (index > -1) {
				this.customer.tags.splice(index, 1);
			} else {
				this.customer.tags.push(tag);
			}

			this.isSaving = true;

			this.TagsService.updateCustomer({
					id: this.customer.id,
					tags: this.customer.tags
				}
			)
				.then(() => {
					this.ToastService.show('Tags saved');
				}).catch((err) => {
					this.ToastService.showError('Failed to update customer tags: ', err);
				}).finally(() => {
					this.isSaving = false;
				});
		};

		selectSrTag(tag: Tag) {
			let index = this.getSrTagIndex(tag.id);

			if (index > -1) {
				this.serviceRequest.tags.splice(index, 1);
			} else {
				this.serviceRequest.tags.push(tag);
			}

			this.isSaving = true;

			this.TagsService.updateSr({
					id: this.serviceRequest.id,
					tags: this.serviceRequest.tags
				}
			)
				.then(() => {
					this.ToastService.show('Tags saved');
				}).catch((err) => {
				this.ToastService.showError('Failed to update SR tags: ', err);
			}).finally(() => {
				this.isSaving = false;
			});
		};

		updateRecolorTag(tag: Tag) {
			this.isSaving = true;

			if (this.customer.recolor_tag && this.customer.recolor_tag.id === tag.id) {
				tag = null;
			}

			this.TagsService.updateCustomer({
					id: this.customer.id,
					recolor_tag: tag
				}
			)
				.then(() => {
					this.ToastService.show('Recolor saved');
					this.customer.recolor_tag = tag;
				}).catch((err) => {
					this.ToastService.showError('Failed to update customer tags: ', err);
				}).finally(() => {
					this.isSaving = false;
				});
		};

		toggleTagEdit(type: string = null, tag: Tag = null) {
			this.tagEdit = !this.tagEdit;
			this.newTag = tag;
			this.newTagType = type;
		};

		editTags() {
			if (this.isSaving) {
				return;
			}

			if (!this.newTag.name || !this.newTag.tag_color) {
				this.ToastService.show('Tag Name and Color are Required.');
				return;
			}

			this.isSaving = true;
			var func = this.newTag.id
				? this.newTagType === 'customer' ? this.TagsService.updateCustomerTag : this.TagsService.updateSRTag
				: this.newTagType === 'customer' ? this.TagsService.createCustomerTag : this.TagsService.createSRTag;

			func.bind(this.TagsService)(this.newTag)
				.then((response) => {
					let index;
					if (this.newTagType === 'customer') {
						index = this.customerTags.findIndex(function(t) {
							return t.id === response.data.id;
						});

						if (index > -1) {
							this.customerTags[index] = response.data;
						} else {
							this.customerTags.push(response.data);
						}
					} else {
						index = this.srTags.findIndex(function (t) {
							return t.id === response.data.id;
						});

						if (index > -1) {
							this.srTags[index] = response.data;
						} else {
							this.srTags.push(response.data);
						}
					}

					this.toggleTagEdit();
					this.ToastService.show('Tag saved');
				})
				.catch((err) => {
					this.ToastService.showError('Failed to save tag: ', err);
				})
				.finally(() => {
					this.isSaving = false;
				});
		};
	}
}
